import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { verifyDeposit } from "../../api/apiServices";

/**
 * DepositVerify
 * Route: /deposit-fiat/verify
 *
 * Paystack redirects here after payment:
 *   /deposit-fiat/verify?reference=DEP-xxx
 *
 * This page verifies the payment with the backend,
 * then shows success or failure — no auto-redirect so
 * the user can see their receipt.
 */
export default function DepositVerify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState("verifying"); // verifying | success | failed
  const [verifiedAmount, setVerifiedAmount] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const reference = searchParams.get("reference");

    if (!reference) {
      setStatus("failed");
      setErrorMsg("No payment reference found.");
      return;
    }

    const verify = async () => {
      try {
        const res = await verifyDeposit(reference);
        if (res.status) {
          setVerifiedAmount(res.amount);
          setStatus("success");
        } else {
          setErrorMsg(res.message || "Payment could not be confirmed.");
          setStatus("failed");
        }
      } catch (err) {
        setErrorMsg(
          err?.response?.data?.message ||
          "Could not verify your payment. If money was deducted, please contact support."
        );
        setStatus("failed");
      }
    };

    verify();
  }, []);

  // ── Verifying ─────────────────────────────────────────────
  if (status === "verifying") {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex flex-col items-center justify-center p-8 text-center"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        <div className="w-20 h-20 bg-white rounded-3xl shadow-lg flex items-center justify-center mb-6 border border-slate-100">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
        <h2 className="text-slate-800 font-bold text-xl mb-2">Confirming payment…</h2>
        <p className="text-slate-400 text-sm max-w-xs leading-relaxed">
          Please wait while we confirm your payment with Paystack. Do not close this page.
        </p>

        {/* Animated steps */}
        <div className="mt-8 space-y-2 text-left w-full max-w-xs">
          {[
            "Connecting to Paystack…",
            "Verifying transaction…",
            "Crediting your wallet…",
          ].map((step, i) => (
            <div key={step} className="flex items-center gap-3 px-4 py-2.5 bg-white rounded-xl border border-slate-100">
              <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin flex-shrink-0"
                style={{ animationDelay: `${i * 0.3}s` }} />
              <span className="text-slate-500 text-xs">{step}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Failed ────────────────────────────────────────────────
  if (status === "failed") {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex flex-col pb-10"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        <div className="bg-gradient-to-br from-red-500 to-rose-600 px-5 pt-16 pb-12 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10"
            style={{ background: "radial-gradient(circle, #fff, transparent)", transform: "translate(30%,-30%)" }} />
          <div className="w-20 h-20 bg-white/20 rounded-full border-4 border-white/30 flex items-center justify-center mx-auto mb-4">
            <i className="ri-close-circle-fill text-white text-4xl" />
          </div>
          <h1 className="text-white text-2xl font-bold mb-1">Payment Failed</h1>
          <p className="text-red-100 text-sm">We could not confirm your payment</p>
        </div>

        <div className="px-5 mt-6 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-lg" />
              </div>
              <div>
                <p className="text-slate-700 font-semibold text-sm mb-1">What happened?</p>
                <p className="text-slate-500 text-xs leading-relaxed">{errorMsg}</p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
            <i className="ri-information-line text-amber-500 text-lg mt-0.5 flex-shrink-0" />
            <p className="text-amber-700 text-xs leading-relaxed">
              If money was deducted from your account but your wallet wasn't credited,
              please contact support with your payment reference.
            </p>
          </div>

          <button
            onClick={() => navigate("/deposit-fiat")}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-bold text-sm transition-all">
            Try Again
          </button>
          <button
            onClick={() => navigate("/support")}
            className="w-full border border-slate-200 text-slate-600 py-4 rounded-2xl font-semibold text-sm transition-all hover:bg-slate-50">
            Contact Support
          </button>
          <button
            onClick={() => navigate("/home")}
            className="w-full text-slate-400 py-2 text-sm">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F7F8FA] flex flex-col pb-10"
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 px-5 pt-16 pb-12 text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #fff, transparent)", transform: "translate(30%,-30%)" }} />
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #fff, transparent)", transform: "translate(-30%,30%)" }} />

        <div className="w-20 h-20 bg-white/20 rounded-full border-4 border-white/30 flex items-center justify-center mx-auto mb-4">
          <i className="ri-checkbox-circle-fill text-white text-4xl" />
        </div>
        <h1 className="text-white text-2xl font-bold mb-1">Deposit Successful!</h1>
        <p className="text-emerald-100 text-sm mb-5">Your NGN wallet has been credited</p>
        <div className="inline-block bg-white/15 rounded-2xl px-6 py-3 border border-white/20">
          <p className="text-emerald-200 text-xs mb-0.5">Amount credited</p>
          <p className="text-white text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            ₦{verifiedAmount.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Receipt card */}
      <div className="px-5 -mt-4 relative z-10">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-lg overflow-hidden">
          {/* Ticket notch effect */}
          <div className="relative h-5 bg-[#F7F8FA]">
            <div className="absolute -bottom-2.5 left-0 right-0 h-5 bg-white rounded-t-3xl" />
            <div className="absolute bottom-0 -left-3 w-6 h-6 bg-[#F7F8FA] rounded-full" />
            <div className="absolute bottom-0 -right-3 w-6 h-6 bg-[#F7F8FA] rounded-full" />
          </div>

          <div className="px-5 pb-6 pt-1">
            <div className="flex justify-center mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <i className="ri-bank-card-line text-emerald-600 text-lg" />
              </div>
            </div>

            <div className="space-y-0">
              {[
                { label: "Credited to wallet", value: `₦${verifiedAmount.toLocaleString()}`, highlight: true },
                { label: "Payment type", value: "Naira Deposit" },
                { label: "Provider", value: "Paystack" },
                { label: "Status", value: "✅ Confirmed" },
                {
                  label: "Date & time",
                  value: new Date().toLocaleString("en-NG", {
                    day: "numeric", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  }),
                },
              ].map((row) => (
                <div key={row.label} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0">
                  <span className="text-slate-400 text-sm">{row.label}</span>
                  <span className={`text-sm font-semibold ${row.highlight ? "text-emerald-600" : "text-slate-800"}`}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 mt-5 space-y-3">
        <button
          onClick={() => navigate("/buy")}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2">
          <i className="ri-swap-box-line" /> Buy Crypto Now
        </button>
        <button
          onClick={() => navigate("/home")}
          className="w-full border border-slate-200 text-slate-600 py-4 rounded-2xl font-semibold text-sm transition-all hover:bg-slate-50">
          Back to Home
        </button>
        <button
          onClick={() => navigate("/deposit-fiat")}
          className="w-full text-slate-400 py-2 text-sm">
          Make Another Deposit
        </button>
      </div>
    </div>
  );
}
